import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import App from './app';
import styles from './styles';

const ShadowApp: React.FC = () => {
  const shadowHostRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<ReactDOM.Root | null>(null);

  useEffect(() => {
    const host = shadowHostRef.current;
    if (!host) return;

    try {
      // attachShadow()は一度実行すると元に戻せないため、StrictModeの
      // mount→cleanup→remountパターンでも同じshadowRootを再利用する。
      // 既存コード(!shadowRootでの早期return)だと、2回目のマウントで
      // 既にshadowRootが存在するためReactツリーの再マウントがスキップされ、
      // 1回目のcleanupでunmount済みのウィジェットが空のまま残ってしまう。
      const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

      let appContainer = shadowRoot.querySelector<HTMLDivElement>('[data-otai-app-container]');

      if (!appContainer) {
        // 完全なCSS隔離のための基本スタイル
        const isolationStyles = `
          :host {
            all: initial;
            display: block;
            contain: layout style paint;
            overflow: hidden !important;
            isolation: isolate;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            font-size: 14px;
            line-height: 1.5;
            color: #333;
            background: transparent;
            box-sizing: border-box;
          }
          
          *, *::before, *::after {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          
          /* 外部CSSの継承を完全にブロック */
          div, span, p, a, button, input, textarea, select {
            all: unset;
            display: revert;
            box-sizing: border-box;
          }
        `;

        // 1. 隔離スタイルを最初に注入
        const isolationStyleElement = document.createElement('style');
        isolationStyleElement.textContent = isolationStyles;
        shadowRoot.appendChild(isolationStyleElement);

        // 2. メインスタイルを注入
        const mainStyleElement = document.createElement('style');
        mainStyleElement.textContent = styles;
        shadowRoot.appendChild(mainStyleElement);

        // 4. アプリケーションコンテナ作成
        appContainer = document.createElement('div');
        appContainer.setAttribute('data-otai-app-container', '');
        appContainer.style.cssText = `
          height: 100%;
          width: 100%;
          position: relative;
          display: block;
          overflow: hidden;
          isolation: isolate;
          contain: layout style paint;
          pointer-events: none;
        `;
        shadowRoot.appendChild(appContainer);
      }

      // 3. @emotion キャッシュ設定
      const emotionCache = createCache({
        key: 'shadow-otai',
        container: shadowRoot,
        prepend: false,
        speedy: false, // 開発時のデバッグを容易にする
      });

      // 5. React アプリをマウント
      // StrictModeを削除してAPI重複呼び出しを防止
      const root = ReactDOM.createRoot(appContainer);
      rootRef.current = root;

      root.render(
        <CacheProvider value={emotionCache}>
          <App />
        </CacheProvider>
      );

    } catch (error) {
      console.error('Shadow DOM initialization failed:', error);

      // フォールバック: 通常のDOM要素として作成
      const fallbackContainer = shadowHostRef.current;
      if (fallbackContainer) {
        fallbackContainer.style.cssText = `
          position: relative;
          z-index: 999999;
          height: 100%;
          width: 100%;
        `;

        const root = ReactDOM.createRoot(fallbackContainer);
        rootRef.current = root;
        root.render(
          <>
            <App />
          </>
        );

        console.warn('Using fallback DOM mode');
      }
    }

    // ShadowApp自体がアンマウントされた場合、内部のReactツリーとその
    // リスナー・タイマーが破棄されずに残ってしまうため、マウントした
    // Reactルートを確実にunmountする
    return () => {
      rootRef.current?.unmount();
      rootRef.current = null;
    };
  }, []);

  return (
    <div
      ref={shadowHostRef}
      style={{
        height: '100%',
        overflow: 'hidden', // オーバーフロー防止
        width: '100%',
        position: 'relative',
        display: 'block',
        isolation: 'isolate', // CSS containment
        pointerEvents: 'none' // クリックを透過（子要素のみ操作可能）
      }}
    />
  );
};

export default ShadowApp;
